import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, Platform } from '../../components/Icons.jsx';
import { notify } from '@/lib/notifications';
import { ChipInput } from './shared/ChipInput.jsx';
import { fromBrandForm, isBrandProfileReady, toBrandForm } from './shared/brandFormUtils.js';
import {
  GOAL_OPTIONS,
  TONE_OPTIONS,
  VISUAL_STYLE_OPTIONS,
  LANGUAGE_OPTIONS,
  PLATFORMS,
  PLATFORM_LABELS,
} from './shared/platformLabels.js';
import { api } from '../../services/api/client.js';

const REFERENCE_TYPE_OPTIONS = [
  ['logo', 'Logo'],
  ['product', 'Product image'],
  ['previous_post', 'Previous post'],
  ['style_reference', 'Style reference'],
  ['website_screenshot', 'Website screenshot'],
  ['brand_screenshot', 'Brand screenshot'],
  ['other', 'Other reference'],
];

export function BrandIdentityTab({ form, setForm, onSaved, onAskAI, socialAccounts = [], onConnectPlatform }) {
  const { t } = useTranslation();
  const [original, setOriginal] = useState(form);
  const [saving, setSaving] = useState(false);
  const [referenceType, setReferenceType] = useState('style_reference');
  const [referenceUrl, setReferenceUrl] = useState('');
  const [referenceNotes, setReferenceNotes] = useState('');
  const [referenceFile, setReferenceFile] = useState(null);
  const [referenceBusy, setReferenceBusy] = useState(false);

  useEffect(() => {
    setOriginal(form);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(original), [form, original]);
  const ready = isBrandProfileReady(form);

  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const goalsArray = useMemo(
    () => String(form.goalsText || '').split(',').map((g) => g.trim()).filter(Boolean),
    [form.goalsText],
  );
  const setGoals = (next) => update('goalsText', next.join(', '));

  const platformsArray = useMemo(
    () => String(form.preferredPlatformsText || '').split(',').map((g) => g.trim()).filter(Boolean),
    [form.preferredPlatformsText],
  );
  const platformPriorityArray = useMemo(
    () => String(form.platformPriorityText || '').split(',').map((g) => g.trim()).filter(Boolean),
    [form.platformPriorityText],
  );
  const togglePlatform = (id) => {
    const next = platformsArray.includes(id)
      ? platformsArray.filter((x) => x !== id)
      : [...platformsArray, id];
    update('preferredPlatformsText', next.join(', '));
  };

  const forbiddenArray = useMemo(
    () => String(form.forbiddenTopicsText || '').split(',').map((g) => g.trim()).filter(Boolean),
    [form.forbiddenTopicsText],
  );
  const setForbidden = (next) => update('forbiddenTopicsText', next.join(', '));
  const sourceStats = useMemo(() => sourceStatsFor(form), [form]);
  const liveSocialStatus = useMemo(() => buildSocialConnectStatus(socialAccounts, platformsArray), [socialAccounts, platformsArray]);

  async function save() {
    if (!ready) {
      notify('brand.missingRequired');
      return;
    }
    try {
      setSaving(true);
      const result = await api.profile.updateBrandProfile({
        ...fromBrandForm(form),
        socialConnectStatus: { ...(form.socialConnectStatus || {}), ...liveSocialStatus },
      });
      const next = toBrandForm(result.brandProfile);
      setForm(next);
      setOriginal(next);
      onSaved?.(result.brandProfile);
      notify('brand.saved');
    } catch (err) {
      notify('brand.saveFailed', { error: err });
    } finally {
      setSaving(false);
    }
  }

  function cancel() {
    setForm(original);
  }

  function selectReferenceFile(event) {
    const file = event.target.files?.[0] || null;
    if (file && file.size > 5 * 1024 * 1024) {
      event.target.value = '';
      setReferenceFile(null);
      notify('brand.referenceTooLarge');
      return;
    }
    setReferenceFile(file);
  }

  async function addReference() {
    if (referenceBusy) return;
    const url = referenceUrl.trim();
    if (!referenceFile && !url) {
      notify('brand.referenceMissing');
      return;
    }
    try {
      setReferenceBusy(true);
      const payload = {
        type: referenceType,
        notes: referenceNotes.trim() || null,
        url: referenceFile ? undefined : url,
      };
      if (referenceFile) {
        payload.fileName = referenceFile.name;
        payload.mimeType = referenceFile.type;
        payload.dataUrl = await readFileAsDataUrl(referenceFile);
      }
      const result = await api.profile.addBrandReference(payload);
      const next = toBrandForm(result.brandProfile);
      setForm(next);
      setOriginal(next);
      onSaved?.(result.brandProfile);
      setReferenceUrl('');
      setReferenceNotes('');
      setReferenceFile(null);
      notify('brand.referenceSaved', { description: referenceToastText(result.reference) });
    } catch (err) {
      notify('brand.referenceSaveFailed', { error: err });
    } finally {
      setReferenceBusy(false);
    }
  }

  const descCount = String(form.businessDescription || '').length;
  const sectionStatus = useMemo(() => buildSectionStatus(form, t), [form, t]);
  const missingRequired = sectionStatus.filter((s) => s.required && !s.ready);
  const [openSection, setOpenSection] = useState(() => missingRequired[0]?.id || '');

  const toneLabel = optionLabel(TONE_OPTIONS, form.toneOfVoice, t);
  const visualLabel = optionLabel(VISUAL_STYLE_OPTIONS, form.visualStyle, t);
  const languageLabel = optionLabel(LANGUAGE_OPTIONS, form.language, t);
  const platformPreview = platformsArray.length
    ? platformsArray.map((id) => PLATFORM_LABELS[id] || id).slice(0, 3).join(', ')
    : t('brandIdentityTab.noPreferredChannels');
  const goalsPreview = goalsArray.length
    ? goalsArray.map((value) => optionLabel(GOAL_OPTIONS, value, t)).slice(0, 3).join(', ')
    : t('brandIdentityTab.noGoalsSelected');
  const guardrailsPreview = form.contentPillars?.length
    ? t('brandIdentityTab.pillarsSet', { n: form.contentPillars.length })
    : forbiddenArray.length
      ? t('brandIdentityTab.blockedTopics', { count: forbiddenArray.length })
      : t('brandIdentityTab.noGuardrailsAdded');
  const sourceFor = (field) => sourceMetaFor(form, field);

  return (
    <div className="cp-brand-editor">
      <header className="cp-brand-head">
        <div>
          <div className="settings-section-label">{t('brandIdentityTab.brandInputs')}</div>
          <h3>{missingRequired.length ? t('brandIdentityTab.requiredInputsMissing', { count: missingRequired.length }) : t('brandIdentityTab.businessBrainReady')}</h3>
          <p>{t('brandIdentityTab.profileSourceOfTruth')}</p>
        </div>
        <button className="btn btn-secondary" onClick={() => onAskAI?.({ scope: 'content-plan.brand' })}>
          <Icon name="sparkles" size={13} /> {t('brandIdentityTab.askAi')}
        </button>
      </header>

      <div className="cp-brand-progress" aria-label={t('brandIdentityTab.brandInputCoverage')} style={{ '--brand-step-count': sectionStatus.length }}>
        {sectionStatus.map((section) => (
          <span
            key={section.id}
            className={'cp-brand-progress-step ' + (section.ready ? 'is-ready ' : '') + (section.empty ? 'is-empty ' : '') + (section.required && !section.ready ? 'needs-input' : '')}
            title={`${section.title}: ${section.status}`}
          />
        ))}
      </div>

      <BusinessBrainSummary form={form} sourceStats={sourceStats} socialStatus={liveSocialStatus} />

      <div className="cp-brand-list">
        <BrandSection
          id="basics"
          title={t('brandIdentityTab.sectionBusinessTitle')}
          description={t('brandIdentityTab.sectionBusinessDescription')}
          preview={compact(form.businessName || form.productOrService || form.businessDescription, t('brandIdentityTab.needsBusinessContext'))}
          section={sectionStatus.find((s) => s.id === 'basics')}
          open={openSection === 'basics'}
          onToggle={setOpenSection}
          onAskAI={onAskAI}
        >
          <div className="cp-form-grid">
            <Field label={t('brandIdentityTab.businessName')} source={sourceFor('businessName')}>
              <input className="input form-input" value={form.businessName} onChange={(e) => update('businessName', e.target.value)} placeholder={t('brandIdentityTab.businessNamePlaceholder')} />
            </Field>
            <Field label={t('brandIdentityTab.productOrService')} source={sourceFor('productOrService')}>
              <input className="input form-input" value={form.productOrService} onChange={(e) => update('productOrService', e.target.value)} placeholder={t('brandIdentityTab.productOrServicePlaceholder')} />
            </Field>
          </div>
          <Field label={t('brandIdentityTab.businessDescription')} required hint={t('brandIdentityTab.businessDescriptionHint', { chars: descCount })} source={sourceFor('businessDescription')}>
            <textarea
              className="input textarea form-input cp-textarea-locked"
              value={form.businessDescription}
              onChange={(e) => update('businessDescription', e.target.value.slice(0, 500))}
              rows={3}
              placeholder={t('brandIdentityTab.businessDescriptionPlaceholder')}
            />
          </Field>
        </BrandSection>

        <BrandSection
          id="audience"
          title={t('brandIdentityTab.sectionAudienceTitle')}
          description={t('brandIdentityTab.sectionAudienceDescription')}
          preview={compact(form.targetAudience, t('brandIdentityTab.audienceNeeded'))}
          section={sectionStatus.find((s) => s.id === 'audience')}
          open={openSection === 'audience'}
          onToggle={setOpenSection}
          onAskAI={onAskAI}
        >
          <Field label={t('brandIdentityTab.targetAudience')} required source={sourceFor('targetAudience')}>
            <textarea className="input textarea form-input cp-textarea-locked" value={form.targetAudience} onChange={(e) => update('targetAudience', e.target.value)} rows={3} placeholder={t('brandIdentityTab.targetAudiencePlaceholder')} />
          </Field>
          <Field label={t('brandIdentityTab.buyerPains')} source={sourceFor('buyerPains')}>
            <ChipInput value={form.buyerPains} onChange={(v) => update('buyerPains', v)} placeholder={t('brandIdentityTab.buyerPainsPlaceholder')} />
          </Field>
          <Field label={t('brandIdentityTab.objections')} source={sourceFor('objections')}>
            <ChipInput value={form.objections} onChange={(v) => update('objections', v)} placeholder={t('brandIdentityTab.objectionsPlaceholder')} />
          </Field>
        </BrandSection>

        <BrandSection
          id="offer"
          title={t('brandIdentityTab.sectionOfferTitle')}
          description={t('brandIdentityTab.sectionOfferDescription')}
          preview={compact(form.offer, t('brandIdentityTab.noOfferAdded'))}
          section={sectionStatus.find((s) => s.id === 'offer')}
          open={openSection === 'offer'}
          onToggle={setOpenSection}
          onAskAI={onAskAI}
        >
          <Field label={t('brandIdentityTab.yourOffer')} source={sourceFor('offer')}>
            <textarea className="input textarea form-input cp-textarea-locked" value={form.offer} onChange={(e) => update('offer', e.target.value)} rows={3} placeholder={t('brandIdentityTab.yourOfferPlaceholder')} />
          </Field>
        </BrandSection>

        <BrandSection
          id="goals"
          title={t('brandIdentityTab.sectionStrategyTitle')}
          description={t('brandIdentityTab.sectionStrategyDescription')}
          preview={compact(form.positioning, goalsPreview)}
          section={sectionStatus.find((s) => s.id === 'goals')}
          open={openSection === 'goals'}
          onToggle={setOpenSection}
          onAskAI={onAskAI}
        >
          <Field label={t('brandIdentityTab.goals')} hint={t('brandIdentityTab.goalsHint')} source={sourceFor('goals')}>
            <div className="cp-chip-toggle-row">
              {GOAL_OPTIONS.map((opt) => {
                const on = goalsArray.includes(opt.value);
                return <button type="button" key={opt.value} onClick={() => setGoals(on ? goalsArray.filter((x) => x !== opt.value) : [...goalsArray, opt.value])} className={'chip cp-chip-toggle ' + (on ? 'chip-active' : '')}>{opt.label}</button>;
              })}
            </div>
          </Field>
          <Field label={t('brandIdentityTab.positioning')} source={sourceFor('positioning')}>
            <textarea className="input textarea form-input cp-textarea-locked" value={form.positioning} onChange={(e) => update('positioning', e.target.value)} rows={3} placeholder={t('brandIdentityTab.positioningPlaceholder')} />
          </Field>
          <Field label={t('brandIdentityTab.uniqueSellingPoints')} source={sourceFor('uniqueSellingPoints')}>
            <ChipInput value={form.uniqueSellingPoints} onChange={(v) => update('uniqueSellingPoints', v)} placeholder={t('brandIdentityTab.uniqueSellingPointsPlaceholder')} />
          </Field>
          <Field label={t('brandIdentityTab.conversionActions')} source={sourceFor('conversionActions')}>
            <ChipInput value={form.conversionActions} onChange={(v) => update('conversionActions', v)} placeholder={t('brandIdentityTab.conversionActionsPlaceholder')} />
          </Field>
        </BrandSection>

        <BrandSection
          id="voice"
          title={t('brandIdentityTab.sectionToneTitle')}
          description={t('brandIdentityTab.sectionToneDescription')}
          preview={t('brandIdentityTab.tonePreview', { tone: toneLabel, n: form.wordsToAvoid?.length || 0 })}
          section={sectionStatus.find((s) => s.id === 'voice')}
          open={openSection === 'voice'}
          onToggle={setOpenSection}
          onAskAI={onAskAI}
        >
          <div className="cp-form-grid">
            <Field label={t('brandIdentityTab.toneOfVoice')} source={sourceFor('toneOfVoice')}>
              <select className="input select form-input" value={form.toneOfVoice} onChange={(e) => update('toneOfVoice', e.target.value)}>
                {TONE_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </Field>
          </div>
          {form.toneOfVoice === 'custom' && (
            <Field label={t('brandIdentityTab.customToneNotes')} source={sourceFor('customToneDescription')}>
              <textarea className="input textarea form-input cp-textarea-locked" value={form.customToneDescription} onChange={(e) => update('customToneDescription', e.target.value)} rows={3} placeholder={t('brandIdentityTab.customToneNotesPlaceholder')} />
            </Field>
          )}
          <Field label={t('brandIdentityTab.wordsToUse')} source={sourceFor('wordsToUse')}>
            <ChipInput value={form.wordsToUse} onChange={(v) => update('wordsToUse', v)} placeholder={t('brandIdentityTab.wordsToUsePlaceholder')} />
          </Field>
          <Field label={t('brandIdentityTab.wordsToAvoid')} source={sourceFor('wordsToAvoid')}>
            <ChipInput value={form.wordsToAvoid} onChange={(v) => update('wordsToAvoid', v)} placeholder={t('brandIdentityTab.wordsToAvoidPlaceholder')} />
          </Field>
        </BrandSection>

        <BrandSection
          id="guardrails"
          title={t('brandIdentityTab.sectionGuardrailsTitle')}
          description={t('brandIdentityTab.sectionGuardrailsDescription')}
          preview={guardrailsPreview}
          section={sectionStatus.find((s) => s.id === 'guardrails')}
          open={openSection === 'guardrails'}
          onToggle={setOpenSection}
          onAskAI={onAskAI}
        >
          <Field label={t('brandIdentityTab.contentPillars')} hint={t('brandIdentityTab.contentPillarsHint')} source={sourceFor('contentPillars')}>
            <ChipInput value={form.contentPillars} onChange={(v) => update('contentPillars', v)} placeholder={t('brandIdentityTab.contentPillarsPlaceholder')} />
          </Field>
          <Field label={t('brandIdentityTab.forbiddenTopics')} hint={t('brandIdentityTab.forbiddenTopicsHint')} source={sourceFor('forbiddenTopics')}>
            <ChipInput value={forbiddenArray} onChange={setForbidden} placeholder={t('brandIdentityTab.forbiddenTopicsPlaceholder')} />
          </Field>
          <Field label={t('brandIdentityTab.complianceBoundaries')} source={sourceFor('complianceBoundaries')}>
            <ChipInput value={form.complianceBoundaries} onChange={(v) => update('complianceBoundaries', v)} placeholder={t('brandIdentityTab.complianceBoundariesPlaceholder')} />
          </Field>
        </BrandSection>

        <BrandSection
          id="visual"
          title={t('brandIdentityTab.sectionVisualTitle')}
          description={t('brandIdentityTab.sectionVisualDescription')}
          preview={t('brandIdentityTab.visualPreview', { style: visualLabel, n: form.referenceAssets?.length || form.referenceAssetIds?.length || 0 })}
          section={sectionStatus.find((s) => s.id === 'visual')}
          open={openSection === 'visual'}
          onToggle={setOpenSection}
          onAskAI={onAskAI}
        >
          <div className="cp-form-grid">
            <Field label={t('brandIdentityTab.visualStyle')} source={sourceFor('visualStyle')}>
              <select className="input select form-input" value={form.visualStyle} onChange={(e) => update('visualStyle', e.target.value)}>
                {VISUAL_STYLE_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </Field>
            <Field label={t('brandIdentityTab.preferredImageMood')} source={sourceFor('preferredImageMood')}>
              <input className="input form-input" value={form.preferredImageMood} onChange={(e) => update('preferredImageMood', e.target.value)} placeholder={t('brandIdentityTab.preferredImageMoodPlaceholder')} />
            </Field>
          </div>
          <Field label={t('brandIdentityTab.brandColors')} source={sourceFor('brandColors')}>
            <ChipInput value={form.brandColors} onChange={(v) => update('brandColors', v)} placeholder={t('brandIdentityTab.brandColorsPlaceholder')} />
            <label className="cp-color-apply">
              <input
                type="checkbox"
                checked={form.applyBrandColors !== false}
                onChange={(e) => update('applyBrandColors', e.target.checked)}
                style={{ accentColor: 'var(--p-500)', width: 16, height: 16, marginTop: 2, flexShrink: 0 }}
              />
              <span className="cp-color-apply-text">
                <span className="cp-color-apply-title">{t('brandIdentityTab.applyBrandColors')}</span>
                <span className="cp-color-apply-hint">{t('brandIdentityTab.applyBrandColorsHint')}</span>
              </span>
            </label>
          </Field>
          <Field label={t('brandIdentityTab.imageCompositionHints')} source={sourceFor('imageCompositionHints')}>
            <ChipInput value={form.imageCompositionHints} onChange={(v) => update('imageCompositionHints', v)} placeholder={t('brandIdentityTab.imageCompositionHintsPlaceholder')} />
          </Field>
          <Field label={t('brandIdentityTab.avoidVisuals')} source={sourceFor('avoidVisuals')}>
            <ChipInput value={form.avoidVisuals} onChange={(v) => update('avoidVisuals', v)} placeholder={t('brandIdentityTab.avoidVisualsPlaceholder')} />
          </Field>
          <div className="cp-form-grid">
            <Field label={t('brandIdentityTab.logoAssetId')} source={sourceFor('logoAssetId')}>
              <input className="input form-input" value={form.logoAssetId} onChange={(e) => update('logoAssetId', e.target.value)} placeholder={t('brandIdentityTab.logoAssetIdPlaceholder')} />
            </Field>
            <Field label={t('brandIdentityTab.referenceAssetIds')} source={sourceFor('referenceAssetIds')}>
              <ChipInput value={form.referenceAssetIds} onChange={(v) => update('referenceAssetIds', v)} placeholder={t('brandIdentityTab.referenceAssetIdsPlaceholder')} />
            </Field>
          </div>
          <ReferenceDerivedList references={form.referenceAssets} />
          <BrandReferenceUploader
            type={referenceType}
            onTypeChange={setReferenceType}
            url={referenceUrl}
            onUrlChange={setReferenceUrl}
            notes={referenceNotes}
            onNotesChange={setReferenceNotes}
            file={referenceFile}
            onFileChange={selectReferenceFile}
            onAdd={addReference}
            busy={referenceBusy}
          />
        </BrandSection>

        <BrandSection
          id="distribution"
          title={t('brandIdentityTab.sectionDistributionTitle')}
          description={t('brandIdentityTab.sectionDistributionDescription')}
          preview={`${platformPreview} / ${languageLabel}`}
          section={sectionStatus.find((s) => s.id === 'distribution')}
          open={openSection === 'distribution'}
          onToggle={setOpenSection}
          onAskAI={onAskAI}
        >
          <Field label={t('brandIdentityTab.preferredPlatforms')} hint={t('brandIdentityTab.preferredPlatformsHint')} source={sourceFor('preferredPlatforms')}>
            <div className="cp-chip-toggle-row">
              {PLATFORMS.map((id) => {
                const on = platformsArray.includes(id);
                return <button type="button" key={id} onClick={() => togglePlatform(id)} className={'chip cp-chip-toggle ' + (on ? 'chip-active' : '')}>{PLATFORM_LABELS[id]}</button>;
              })}
            </div>
          </Field>
          <Field label={t('brandIdentityTab.platformPriority')} source={sourceFor('platformPriority')}>
            <div className="cp-chip-toggle-row">
              {PLATFORMS.map((id) => {
                const on = platformPriorityArray.includes(id);
                const next = on ? platformPriorityArray.filter((x) => x !== id) : [...platformPriorityArray, id];
                return <button type="button" key={id} onClick={() => update('platformPriorityText', next.join(', '))} className={'chip cp-chip-toggle ' + (on ? 'chip-active' : '')}>{PLATFORM_LABELS[id]}</button>;
              })}
            </div>
          </Field>
          <Field label={t('brandIdentityTab.perPlatformTone')} hint={t('brandIdentityTab.perPlatformToneHint')} source={sourceFor('perPlatformTone')}>
            <textarea className="input textarea form-input" value={form.perPlatformToneText} onChange={(e) => update('perPlatformToneText', e.target.value)} rows={3} placeholder={t('brandIdentityTab.perPlatformTonePlaceholder')} />
          </Field>
          <Field label={t('brandIdentityTab.perPlatformFormatPreferences')} hint={t('brandIdentityTab.perPlatformFormatPreferencesHint')} source={sourceFor('perPlatformFormatPreferences')}>
            <textarea className="input textarea form-input" value={form.perPlatformFormatPreferencesText} onChange={(e) => update('perPlatformFormatPreferencesText', e.target.value)} rows={3} placeholder={t('brandIdentityTab.perPlatformFormatPreferencesPlaceholder')} />
          </Field>
          <PlatformConnectionCtas platforms={platformsArray} socialStatus={liveSocialStatus} onConnect={onConnectPlatform} />
          <WebsiteDerivedPanel form={form} />
          <div className="cp-form-grid">
            <Field label={t('brandIdentityTab.language')} source={sourceFor('language')}>
              <select className="input select form-input" value={form.language} onChange={(e) => update('language', e.target.value)}>
                {LANGUAGE_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </Field>
            <Field label={t('brandIdentityTab.timezone')} hint={t('brandIdentityTab.timezoneHint')} source={sourceFor('timezone')}>
              <input className="input form-input" value={form.timezone} onChange={(e) => update('timezone', e.target.value)} placeholder="Europe/Kyiv" />
            </Field>
          </div>
        </BrandSection>
      </div>

      {dirty && (
        <div className="cp-sticky-savebar">
          <button className="btn btn-secondary" onClick={cancel} disabled={saving}>
            <Icon name="refresh" size={13} /> {t('brandIdentityTab.cancel')}
          </button>
          <button className="btn btn-primary" onClick={save} disabled={saving || !ready}>
            <Icon name="check" size={13} /> {saving ? t('brandIdentityTab.saving') : t('brandIdentityTab.saveChanges')}
          </button>
        </div>
      )}
    </div>
  );
}

function BrandSection({ id, title, description, preview, section, open, onToggle, onAskAI, children }) {
  const { t } = useTranslation();
  const status = section?.status || (section?.ready ? t('brandIdentityTab.statusDone') : t('brandIdentityTab.statusNeedsInput'));
  const needsInput = section?.required && !section?.ready;
  const statusClass = needsInput ? 'needs-input' : section?.empty ? 'is-empty' : section?.ready ? 'is-done' : '';
  return (
    <section className={'cp-brand-section ' + (open ? 'is-open ' : '') + (section?.ready ? 'is-ready ' : '') + (section?.empty ? 'is-empty ' : '') + (needsInput ? 'needs-input' : '')} data-section={id}>
      <button type="button" className="cp-brand-section-trigger" onClick={() => onToggle(open ? '' : id)} aria-expanded={open}>
        <span className={'cp-node-dot ' + (section?.ready ? 'is-ready ' : '') + (section?.empty ? 'is-empty' : '')} />
        <span className="cp-module-title-block cp-brand-title-block">
          <span className="cp-brand-title-line">
            <strong>{title}</strong>
            <span className={'cp-brand-state ' + statusClass}>
              {section?.ready && <Icon name="check" size={11} />}
              {status}
            </span>
          </span>
          <span>{description}</span>
        </span>
        <span className="cp-brand-preview"><span>{preview}</span></span>
        {needsInput ? <span className="cp-brand-row-action needs-input">{t('brandIdentityTab.fill')}</span> : <span className="cp-brand-row-action is-empty" aria-hidden="true" />}
        <Icon name={open ? 'chev_u' : 'chev_d'} size={14} />
      </button>
      {open && (
        <div className="cp-brand-section-body">
          {children}
          <div className="cp-assist-strip">
            <button className="btn btn-ghost btn-sm" onClick={() => onAskAI?.({ scope: `content-plan.brand.${id}` })}>
              <Icon name="sparkles" size={12} /> {t('brandIdentityTab.askAiToImprove')}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function buildSectionStatus(form, t) {
  const has = (value) => String(value || '').trim().length > 0;
  const hasPillars = Array.isArray(form.contentPillars) && form.contentPillars.length > 0;
  const hasForbidden = has(form.forbiddenTopicsText);
  const hasDistribution = has(form.language) && has(form.timezone);
  const hasGoals = String(form.goalsText || '').split(',').some((g) => g.trim());
  const hasStrategy = hasGoals || has(form.positioning) || form.uniqueSellingPoints?.length || form.conversionActions?.length;
  const hasOffer = has(form.offer);
  const hasVisual = has(form.visualStyle) || form.brandColors?.length || form.referenceAssetIds?.length || form.referenceAssets?.length;
  return [
    { id: 'basics', title: t('brandIdentityTab.sectionBusinessTitle'), required: true, ready: has(form.businessDescription), status: has(form.businessDescription) ? t('brandIdentityTab.statusDone') : t('brandIdentityTab.statusNeedsInput') },
    { id: 'audience', title: t('brandIdentityTab.sectionAudienceTitle'), required: true, ready: has(form.targetAudience), status: has(form.targetAudience) ? t('brandIdentityTab.statusDone') : t('brandIdentityTab.statusNeedsInput') },
    { id: 'offer', title: t('brandIdentityTab.sectionOfferTitle'), ready: hasOffer, empty: !hasOffer, status: hasOffer ? t('brandIdentityTab.statusSet') : t('brandIdentityTab.statusNotSet') },
    { id: 'goals', title: t('brandIdentityTab.sectionStrategyTitle'), ready: hasStrategy, empty: !hasStrategy, status: hasStrategy ? t('brandIdentityTab.statusSet') : t('brandIdentityTab.statusNotSet') },
    { id: 'voice', title: t('brandIdentityTab.sectionToneTitle'), ready: has(form.toneOfVoice), status: has(form.toneOfVoice) ? t('brandIdentityTab.statusDone') : t('brandIdentityTab.statusNeedsReview') },
    { id: 'guardrails', title: t('brandIdentityTab.sectionGuardrailsTitle'), ready: hasPillars || hasForbidden, empty: !(hasPillars || hasForbidden), status: hasPillars || hasForbidden ? t('brandIdentityTab.statusSet') : t('brandIdentityTab.statusNotSet') },
    { id: 'visual', title: t('brandIdentityTab.sectionVisualTitle'), ready: hasVisual, empty: !hasVisual, status: hasVisual ? t('brandIdentityTab.statusSet') : t('brandIdentityTab.statusNotSet') },
    { id: 'distribution', title: t('brandIdentityTab.sectionDistributionTitle'), ready: hasDistribution, empty: !hasDistribution, status: hasDistribution ? t('brandIdentityTab.statusDone') : t('brandIdentityTab.statusNotSet') },
  ];
}

function Field({ label, required, hint, source, children }) {
  return (
    <div className="form-field cp-field">
      <label>
        {label}
        {required && <span className="cp-required"> *</span>}
        {source && <SourceBadge meta={source} />}
      </label>
      {children}
      {hint && <div className="helper">{hint}</div>}
    </div>
  );
}

function BusinessBrainSummary({ form, sourceStats, socialStatus }) {
  const { t } = useTranslation();
  const connectedCount = Object.values(socialStatus || {}).filter((item) => item?.connected).length;
  return (
    <div className="cp-brain-summary">
      <div>
        <span className="settings-section-label">{t('brandIdentityTab.businessBrain')}</span>
        <strong>{sourceStats.reviewCount ? t('brandIdentityTab.fieldsNeedReview', { n: sourceStats.reviewCount }) : t('brandIdentityTab.confirmedSourceOfTruth')}</strong>
        <p>{form.websiteUrl ? t('brandIdentityTab.websiteContext', { url: form.websiteUrl }) : t('brandIdentityTab.addWebsiteContext')}</p>
      </div>
      <div className="cp-brain-metrics">
        <Metric label={t('brandIdentityTab.metricAiSuggested')} value={sourceStats.aiCount} />
        <Metric label={t('brandIdentityTab.metricWebsite')} value={sourceStats.websiteCount} />
        <Metric label={t('brandIdentityTab.metricReferences')} value={sourceStats.referenceCount} />
        <Metric label={t('brandIdentityTab.metricConnected')} value={connectedCount} />
      </div>
    </div>
  );
}

function Metric({ label, value }) {
  return <span><strong>{value}</strong>{label}</span>;
}

function SourceBadge({ meta }) {
  const { t } = useTranslation();
  const source = sourceLabel(meta?.source, t);
  const confidence = Number(meta?.confidence);
  const needsReview = meta?.needsReview === true || (Number.isFinite(confidence) && confidence < 0.72);
  return (
    <span className={'cp-source-badge ' + (needsReview ? 'needs-review' : '')}>
      {source}{Number.isFinite(confidence) ? ` ${Math.round(confidence * 100)}%` : ''}{needsReview ? ` ${t('brandIdentityTab.review')}` : ''}
    </span>
  );
}

function PlatformConnectionCtas({ platforms, socialStatus, onConnect }) {
  const { t } = useTranslation();
  const items = platforms.length ? platforms : ['linkedin', 'instagram'];
  return (
    <div className="cp-platform-cta-grid">
      {items.map((platform) => {
        const connected = socialStatus?.[platform]?.connected;
        return (
          <div key={platform} className="cp-platform-cta">
            <span><Platform name={platform} size={15} /> {PLATFORM_LABELS[platform] || platform}</span>
            {connected ? <strong>{t('brandIdentityTab.connected')}</strong> : <button type="button" className="btn btn-secondary btn-sm" onClick={() => onConnect?.(platform)}>{t('brandIdentityTab.connect')}</button>}
          </div>
        );
      })}
    </div>
  );
}

function WebsiteDerivedPanel({ form }) {
  const { t } = useTranslation();
  if (!form.websiteUrl && !form.websiteAnalysis) return null;
  const title = form.websiteAnalysis?.title || form.websiteAnalysis?.ogTitle || t('brandIdentityTab.websiteAnalyzed');
  const headings = Array.isArray(form.websiteAnalysis?.headings) ? form.websiteAnalysis.headings.slice(0, 4) : [];
  return (
    <div className="cp-derived-panel">
      <strong>{t('brandIdentityTab.websiteDerivedFields')}</strong>
      <span>{form.websiteUrl || title}</span>
      {headings.length > 0 && <p>{headings.join(' / ')}</p>}
    </div>
  );
}

function ReferenceDerivedList({ references }) {
  const { t } = useTranslation();
  const items = Array.isArray(references) ? references : [];
  if (!items.length) return null;
  return (
    <div className="cp-derived-panel">
      <strong>{t('brandIdentityTab.referenceDerivedFields')}</strong>
      <div className="cp-reference-card-grid">
        {items.filter((item) => item.publicUrl).slice(0, 4).map((item) => (
          <div key={item.assetId || item.publicUrl} className="cp-reference-card">
            <img src={item.publicUrl} alt={t('brandIdentityTab.referencePreviewAlt', { type: item.type || t('brandIdentityTab.referenceTypeDefault') })} loading="lazy" />
            <small>{item.analysisStatus === 'analyzed' ? t('brandIdentityTab.analyzed') : t('brandIdentityTab.saved')}</small>
          </div>
        ))}
      </div>
      {items.slice(0, 5).map((item) => <span key={item.assetId || item.publicUrl}>{item.type || t('brandIdentityTab.referenceTypeDefaultLower')}{item.promptGuidance || item.notes ? `: ${item.promptGuidance || item.notes}` : ''}</span>)}
    </div>
  );
}

function BrandReferenceUploader({ type, onTypeChange, url, onUrlChange, notes, onNotesChange, file, onFileChange, onAdd, busy }) {
  const { t } = useTranslation();
  return (
    <div className="cp-reference-uploader">
      <div>
        <strong>{t('brandIdentityTab.addVisualReference')}</strong>
        <p>{t('brandIdentityTab.addVisualReferenceDescription')}</p>
      </div>
      <div className="cp-form-grid">
        <select className="input select form-input" value={type} onChange={(event) => onTypeChange(event.target.value)} disabled={busy}>
          {REFERENCE_TYPE_OPTIONS.map(([value, label]) => <option key={value} value={value}>{t(`brandIdentityTab.referenceType_${value}`, label)}</option>)}
        </select>
        <input className="input form-input" type="url" value={url} onChange={(event) => onUrlChange(event.target.value)} placeholder={t('brandIdentityTab.publicImageUrl')} disabled={busy || Boolean(file)} />
      </div>
      <input className="input form-input" type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={onFileChange} disabled={busy} />
      <BrandReferencePreview file={file} url={url} />
      <textarea className="input textarea form-input" value={notes} onChange={(event) => onNotesChange(event.target.value)} rows={2} placeholder={t('brandIdentityTab.referenceNotesPlaceholder')} disabled={busy} />
      <button type="button" className="btn btn-secondary btn-sm" onClick={onAdd} disabled={busy || (!file && !url.trim())}>
        {busy ? t('brandIdentityTab.savingReference') : t('brandIdentityTab.addReferenceButton')}
      </button>
    </div>
  );
}

function BrandReferencePreview({ file, url }) {
  const [previewUrl, setPreviewUrl] = useState('');
  useEffect(() => {
    if (!file) {
      setPreviewUrl('');
      return undefined;
    }
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);
  const { t } = useTranslation();
  const src = previewUrl || String(url || '').trim();
  if (!src) return null;
  return <img className="cp-reference-preview-img" src={src} alt={t('brandIdentityTab.selectedReferencePreviewAlt')} loading="lazy" />;
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(new Error('Unable to read reference file'));
    reader.readAsDataURL(file);
  });
}

function referenceToastText(reference) {
  if (reference?.analysisStatus === 'analyzed') return 'Visual guidance analyzed and linked to Brand Profile.';
  if (reference?.promptGuidance) return 'Reusable visual guidance saved to Brand Profile.';
  return 'Reference saved to Brand Profile.';
}

function sourceStatsFor(form) {
  const values = Object.values(form.sourceMetadata || {});
  return values.reduce((stats, meta) => {
    const source = meta?.source;
    if (source === 'website') stats.websiteCount += 1;
    if (source === 'uploaded_reference') stats.referenceCount += 1;
    if (source === 'ai_inferred' || source === 'assistant_suggestion') stats.aiCount += 1;
    const confidence = Number(meta?.confidence);
    if (meta?.needsReview === true || (Number.isFinite(confidence) && confidence < 0.72)) stats.reviewCount += 1;
    return stats;
  }, { websiteCount: 0, referenceCount: 0, aiCount: 0, reviewCount: 0 });
}

function sourceMetaFor(form, field) {
  return form.sourceMetadata?.[field] || null;
}

function sourceLabel(source, t) {
  if (source === 'manual') return t('brandIdentityTab.sourceManual');
  if (source === 'website') return t('brandIdentityTab.sourceWebsite');
  if (source === 'uploaded_reference') return t('brandIdentityTab.sourceReference');
  if (source === 'user_answer') return t('brandIdentityTab.sourceUserAnswer');
  if (source === 'assistant_suggestion') return t('brandIdentityTab.sourceAiSuggestion');
  if (source === 'ai_inferred') return t('brandIdentityTab.sourceAiInferred');
  return t('brandIdentityTab.sourceUnknown');
}

function buildSocialConnectStatus(accounts, platforms) {
  const status = {};
  const wanted = platforms.length ? platforms : PLATFORMS;
  for (const platform of wanted) {
    const matches = (accounts || []).filter((account) => normalizePlatform(account.platform) === platform);
    status[platform] = { connected: matches.some((account) => account.status === 'connected' || account.status === 'ready'), count: matches.length };
  }
  return status;
}

function normalizePlatform(value) {
  return String(value || '').toLowerCase() === 'twitter' ? 'x' : String(value || '').toLowerCase();
}

function compact(value, fallback) {
  const text = String(value || '').trim();
  if (!text) return fallback;
  return text.length > 58 ? `${text.slice(0, 55)}...` : text;
}

function optionLabel(options, value, t) {
  return options.find((opt) => opt.value === value)?.label || value || t('brandIdentityTab.auto');
}
